import { useCallback } from 'react'
import { useT } from '@transifex/react'
import { ProductDetails } from '../AddProductToSale/useAddProductToSale'
import { useQkNodesResource } from '../../ng-compat/hooks'
import { quickKeyLayoutsStore } from '../../factories/database/quick-key-layouts-store'
import {
  DEFAULT_COLUMNS,
  DEFAULT_ROWS,
  QuickKeyColour,
} from './utils/qk.constants'
import { ApiLayoutData, ApiNode } from './utils/qk-types'
import { isPlaceholderPage } from './utils/qk-layout'
import { getChildrenForNode } from './quickKeyUtils'

const DEFAULT_ATTRIBUTES = [
  { key: 'color', value: QuickKeyColour.WHITE },
  { key: 'showImage', value: 1 },
  { key: 'columns', value: 1 },
  { key: 'rows', value: 1 },
]

const DEFAULT_FOLDER_ATTRIBUTES = [
  { key: 'color', value: QuickKeyColour.WHITE },
  { key: 'isChild', value: 0 },
  { key: 'columns', value: 1 },
  { key: 'rows', value: 1 },
  { key: 'showImage', value: 1 },
]

interface CreateQuickKeyParams {
  layoutData: ApiLayoutData
  product: ProductDetails
  node: ApiNode
  currentRoot: ApiNode | null
  isChild: boolean
}

const buildQKNode = (
  product: ProductDetails,
  node: ApiNode,
  isChild: boolean
) => {
  const attributes = [
    ...DEFAULT_ATTRIBUTES,
    { key: 'isChild', value: isChild ? '1' : '0' },
    { key: 'label', value: product.label },
  ]
  const newNodeData = {
    parent_node_id: node.parent_node_id,
    position: node.position,
    product_id: product.productId,
    attributes,
  }

  // APiNode type requires an ID but we don't have one until the node is created
  return newNodeData as ApiNode
}

const buildQKPage = (parentId: string | null, position: number) => {
  const newNodeData = {
    parent_node_id: parentId,
    position,
    attributes: [
      { key: 'columns', value: DEFAULT_COLUMNS },
      { key: 'rows', value: DEFAULT_ROWS },
    ],
  }
  return (newNodeData as unknown) as ApiNode
}

const buildQKFolder = (
  label: string,
  parentId: string | null,
  position: number
): ApiNode => {
  const attributes = [
    ...DEFAULT_FOLDER_ATTRIBUTES,
    { key: 'label', value: label },
  ]
  const node = {
    parent_node_id: parentId,
    position,
    attributes,
    product_id: null,
  }
  // APiNode type requires an ID but we don't have one until the node is created
  return node as ApiNode
}

export const useCreateFolder = () => {
  const qkNodesResource = useQkNodesResource()
  const t = useT()

  return useCallback(
    async (node: ApiNode, layoutData: ApiLayoutData) => {
      const folder = buildQKFolder(
        t('New Folder'),
        node.parent_node_id,
        node.position
      )
      const newFolder = await qkNodesResource.createQuickKey(
        layoutData.id,
        folder
      )
      await quickKeyLayoutsStore.put({
        ...layoutData,
        nodes: [...layoutData.nodes, newFolder],
      })
      return newFolder
    },
    [qkNodesResource, t]
  )
}

export const useCreatePage = () => {
  const qkNodesResource = useQkNodesResource()
  return useCallback(
    async (
      parentId: string | null,
      position: number,
      layoutData: ApiLayoutData
    ) => {
      const newPageData = buildQKPage(parentId, position)
      const page = await qkNodesResource.createQuickKey(
        layoutData.id,
        newPageData
      )
      await quickKeyLayoutsStore.put({
        ...layoutData,
        nodes: [...layoutData.nodes, page],
      })
      return page
    },
    [qkNodesResource]
  )
}

export const useCreateQuickKey = () => {
  const qkNodesResource = useQkNodesResource()
  const createPage = useCreatePage()

  return useCallback(
    async ({
      product,
      node,
      layoutData,
      currentRoot,
      isChild,
    }: CreateQuickKeyParams) => {
      if (!node.parent_node_id) {
        throw new Error('Node must have a parent node ID')
      }

      let newPage: ApiNode | undefined
      if (isPlaceholderPage(node)) {
        const rootId = currentRoot ? currentRoot.id : null
        const currentPageCount = getChildrenForNode(layoutData, rootId).length
        newPage = await createPage(rootId, currentPageCount, layoutData)
      }

      const newNodeData = buildQKNode(
        product,
        newPage ? { ...node, parent_node_id: newPage.id } : node,
        isChild
      )

      const newNode: ApiNode = await qkNodesResource.createQuickKey(
        layoutData.id,
        newNodeData
      )

      const newNodes = [...layoutData.nodes, newNode]

      if (newPage) {
        newNodes.push(newPage)
      }

      await quickKeyLayoutsStore.put({
        ...layoutData,
        nodes: newNodes,
      })

      return [newNode, newPage].filter(Boolean) as ApiNode[]
    },
    [createPage, qkNodesResource]
  )
}
